import { Apple, Monitor, Smartphone, Terminal, Download as DownloadIcon, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/SectionHeading";

const platforms = [
  {
    name: "macOS",
    desc: "Universal binary · 12.0+",
    file: "AVault-2.4.dmg",
    icon: Apple,
    primary: true,
  },
  {
    name: "Windows",
    desc: "Windows 10 / 11 · x64",
    file: "AVault-2.4-Setup.exe",
    icon: Monitor,
  },
  {
    name: "Linux",
    desc: "AppImage · deb · rpm",
    file: "AVault-2.4.AppImage",
    icon: Terminal,
  },
  {
    name: "iOS",
    desc: "iPhone & iPad · iOS 16+",
    file: "App Store",
    icon: Smartphone,
  },
  {
    name: "Android",
    desc: "Android 10+ · APK or Play",
    file: "Google Play",
    icon: Smartphone,
  },
];

export default function Download() {
  return (
    <div>
      <section className="py-20 lg:py-28" data-testid="download-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="label-eyebrow mb-5">Download</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
            Get AVault on
            <br />
            <span className="text-mint-600 dark:text-mint">every device</span>.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            One signed binary per platform. Same encrypted .avault format everywhere — no account, no setup tax.
          </p>
        </div>
      </section>

      <section className="pb-24" data-testid="download-platforms">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {platforms.map((p) => (
            <div
              key={p.name}
              className={`glass-card p-7 flex flex-col ${
                p.primary ? "ring-2 ring-mint" : ""
              }`}
              data-testid={`download-card-${p.name.toLowerCase()}`}
            >
              <div className="h-12 w-12 rounded-xl bg-foreground/5 flex items-center justify-center">
                <p.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-heading text-2xl font-bold">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
              <p className="font-mono text-xs text-muted-foreground mt-4 truncate">
                {p.file}
              </p>
              <Button
                className={`mt-6 rounded-full ${
                  p.primary
                    ? "bg-mint text-ink hover:bg-mint-300"
                    : "bg-foreground text-background hover:bg-foreground/90"
                }`}
                data-testid={`download-btn-${p.name.toLowerCase()}`}
              >
                <DownloadIcon className="h-4 w-4 mr-1" />
                Download for {p.name}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24" data-testid="download-verify">
        <div className="max-w-5xl mx-auto px-6 glass-card p-10">
          <SectionHeading
            eyebrow="Verify your build"
            title="Every release is signed and reproducible."
          />
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div>
              <p className="label-eyebrow mb-2">SHA-256 (macOS)</p>
              <p className="font-mono text-xs break-all bg-muted rounded-lg p-3">
                3f1a8b...c9e4 d72b f1ab 9f01 8d3e 4c2a 0f55 8b9c
              </p>
            </div>
            <div>
              <p className="label-eyebrow mb-2">PGP signature</p>
              <p className="font-mono text-xs break-all bg-muted rounded-lg p-3">
                AVault Release Key · 0xC8DFE3A7B2F4
              </p>
            </div>
          </div>
          <div className="mt-8">
            <a href="#" className="inline-flex items-center gap-2 text-sm font-semibold text-mint-700 dark:text-mint hover:underline">
              Read the verification guide <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
